// How We Do It / Elakay Academy -- shared display labels and date formats,
// in the visitor's language (en, fr, es, ht, pt). One place, so the
// dashboard, the editor and the public pages show the same words.
import { getElakayLang } from "../../auth";
import { elakayAutoText } from "../dataElakay";

export const TUTORIAL_LANGUAGES = Object.freeze(["fr", "en", "es", "ht", "pt"]);

const LOCALES = { en: "en-US", fr: "fr-FR", es: "es-ES", ht: "fr-HT", pt: "pt-BR" };

export const currentLanguageCode = () => {
  const stored = String(getElakayLang()?.obj?.code || "").toLowerCase().split("-")[0];
  if (TUTORIAL_LANGUAGES.includes(stored)) return stored;
  const documentLang = typeof document !== "undefined" ? String(document.documentElement.lang || "").toLowerCase().split("-")[0] : "";
  return TUTORIAL_LANGUAGES.includes(documentLang) ? documentLang : "fr";
};

export const currentLocale = () => LOCALES[currentLanguageCode()] || "fr-FR";

export const formatTutorialDate = (value, style = "medium") => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const options = style === "long" ? { year: "numeric", month: "long", day: "numeric" } : { year: "numeric", month: "short", day: "numeric" };
  try {
    return date.toLocaleDateString(currentLocale(), options);
  } catch {
    return date.toLocaleDateString("fr-FR", options);
  }
};

export const formatTutorialTime = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  try {
    return date.toLocaleTimeString(currentLocale(), { hour: "2-digit", minute: "2-digit" });
  } catch {
    return date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  }
};

const STATUS = {
  draft: ["howWeDoIt_status_draft", "Brouillon"],
  review: ["howWeDoIt_status_review", "En revision"],
  published: ["howWeDoIt_status_published", "Publie"],
  archived: ["howWeDoIt_status_archived", "Archive"],
};

export const TUTORIAL_STATUSES = Object.freeze(Object.keys(STATUS));

export const statusLabel = (status) => {
  const entry = STATUS[status];
  return entry ? elakayAutoText(entry[0], entry[1]) : String(status || "");
};

const VISIBILITY = {
  public: ["howWeDoIt_visibility_public", "Public"],
  loggedInUsers: ["howWeDoIt_visibility_loggedIn", "Utilisateurs connectes"],
  selectedRoles: ["howWeDoIt_visibility_roles", "Roles selectionnes"],
  private: ["howWeDoIt_visibility_private", "Prive (equipe Elakay)"],
};

export const TUTORIAL_VISIBILITIES = Object.freeze(Object.keys(VISIBILITY));

export const visibilityLabel = (visibility) => {
  const entry = VISIBILITY[visibility || "public"];
  return entry ? elakayAutoText(entry[0], entry[1]) : String(visibility || "");
};

// Canonical role values (the ones stored in allowedRoles) and their labels.
export const TUTORIAL_ROLES = Object.freeze([
  { value: "Royalty", key: "h_2d0253ae41234a", fallback: "User Royale", aliases: ["User Royale", "Customer"] },
  { value: "Seller", key: "h_ec3ef051d5a8bf", fallback: "Seller", aliases: [] },
  { value: "Provider", key: "h_7ceee3f3615a2b", fallback: "Provider", aliases: ["Producer"] },
  { value: "Associate", key: "h_5f1e87a5d1f90b", fallback: "Associate", aliases: [] },
  { value: "Investment", key: "h_e7190516d1891d", fallback: "Investor", aliases: ["Investor"] },
  { value: "Family", key: "h_4efb6cb7c018f0", fallback: "Developer", aliases: ["Familly"] },
  { value: "Administrator", key: "h_1eda23758be9e3", fallback: "Administrator", aliases: ["Admin"] },
]);

export const roleLabel = (role) => {
  const entry = TUTORIAL_ROLES.find((item) => item.value === role || item.aliases.includes(role));
  return entry ? elakayAutoText(entry.key, entry.fallback) : String(role || "");
};

const LANGUAGE_NAMES = {
  fr: "Francais",
  en: "English",
  es: "Espanol",
  ht: "Kreyol",
  pt: "Portugues",
};

export const languageLabel = (code) => LANGUAGE_NAMES[code] || String(code || "").toUpperCase();
